/**
 * Tipos del reporte logístico pivot (GET /api/v1/report/logistic-pivot)
 */

export type LogisticPivotDimension =
  | 'year'
  | 'month'
  | 'team'
  | 'shop'
  | 'courier'
  | 'courier_service'
  | 'country'
  | 'postal_zone'
  | 'item';

/** Cuando la dimensión `item` está activa (producto, pack o desconocido). */
export type LogisticPivotItemType = 'product' | 'pack' | 'unknown';

export type LogisticPivotDateColumn = 'processed_at' | 'created_at' | 'updated_at' | 'sent_at';

/**
 * Visualización con varias dimensiones:
 * - `columns`: una fila por combinación; cada dimensión en su columna (tabla plana).
 * - `hierarchy`: subtotales por nivel y filas plegables (desglose tipo árbol).
 */
export type LogisticPivotGroupDisplayMode = 'columns' | 'hierarchy';

export const LOGISTIC_PIVOT_DIMENSIONS: LogisticPivotDimension[] = [
  'year',
  'month',
  'team',
  'shop',
  'courier',
  'courier_service',
  'country',
  'postal_zone',
  'item',
];

export interface LogisticPivotQuery {
  start_date?: string;
  end_date?: string;
  date_column?: LogisticPivotDateColumn;
  dimensions?: LogisticPivotDimension[];
  team_id?: number;
  payment_method_id?: number;
  shop_id?: number;
  courier_id?: number;
  country_id?: number;
  courier_service_id?: number;
}

export interface LogisticPivotRowMetrics {
  orders_sent: number;
  orders_delivered: number;
  orders_returned: number;
  /** Puede venir del API; la UI ya no muestra columna dedicada. */
  delivery_rate?: number;
  /** Resto de envíos (p. ej. agregados en ramas); si falta, se calcula en `getPivotMetricNumeric`. */
  orders_remaining?: number;
}

export interface LogisticPivotRow extends LogisticPivotRowMetrics {
  year?: number;
  month?: number;
  team_name?: string | null;
  shop_name?: string | null;
  courier_name?: string | null;
  courier_service?: string | null;
  country?: string | null;
  /** Dimensión `postal_zone`: dos primeras cifras del CP o valor reservado del API. */
  postal_zone?: string | null;
  /** Dimensión `item`: producto o pack unificados. */
  item_type?: LogisticPivotItemType | null;
  item_name?: string | null;
  item_code?: string | null;
}

export interface LogisticPivotFiltersEcho {
  start_date: string;
  end_date: string;
  date_column: string;
  dimensions: string[];
  team_id: number | null;
  payment_method_id: number | null;
  shop_id: number | null;
  courier_id: number | null;
  country_id: number | null;
  courier_service_id: number | null;
}

export interface LogisticPivotData {
  rows: LogisticPivotRow[];
  filters: LogisticPivotFiltersEcho;
}

export interface LogisticPivotSuccessBody {
  success: true;
  data: LogisticPivotData;
}

/** Respuesta GET `/report/logistic-pivot/export`: URL del Excel generado. */
export interface LogisticPivotExportResponse {
  url_file: string;
}

/** Columnas de métricas configurables en la tabla (selector de columnas). */
export type LogisticPivotMetricColumnId =
  | 'orders_sent'
  | 'orders_delivered'
  | 'orders_returned'
  | 'orders_remaining';

export const LOGISTIC_PIVOT_METRIC_COLUMNS: { id: LogisticPivotMetricColumnId; headerKey: string }[] = [
  { id: 'orders_sent', headerKey: 'metrics.orders_sent' },
  { id: 'orders_delivered', headerKey: 'metrics.orders_delivered' },
  { id: 'orders_returned', headerKey: 'metrics.orders_returned' },
  { id: 'orders_remaining', headerKey: 'metrics.orders_remaining' },
];

/** Pedidos enviados que no están ni entregados ni devueltos (resto del embudo). */
export function ordersRemainingFromRow(row: Pick<LogisticPivotRowMetrics, 'orders_sent' | 'orders_delivered' | 'orders_returned'>): number {
  const s = row.orders_sent ?? 0;
  const d = row.orders_delivered ?? 0;
  const r = row.orders_returned ?? 0;
  return Math.max(0, s - d - r);
}

export function getPivotMetricNumeric(
  row: Pick<LogisticPivotRow, keyof LogisticPivotRowMetrics>,
  id: LogisticPivotMetricColumnId
): number {
  switch (id) {
    case 'orders_sent':
      return row.orders_sent ?? 0;
    case 'orders_delivered':
      return row.orders_delivered ?? 0;
    case 'orders_returned':
      return row.orders_returned ?? 0;
    case 'orders_remaining':
      if (typeof row.orders_remaining === 'number') return row.orders_remaining;
      return ordersRemainingFromRow(row);
  }
}

/** Mapeo dimensión API → clave en cada fila (`item` usa varios campos; ver utilidades). */
export const DIMENSION_TO_ROW_KEY: Record<
  LogisticPivotDimension,
  keyof LogisticPivotRow | null
> = {
  year: 'year',
  month: 'month',
  team: 'team_name',
  shop: 'shop_name',
  courier: 'courier_name',
  courier_service: 'courier_service',
  country: 'country',
  postal_zone: 'postal_zone',
  item: null,
};

/** Clave estable para agrupar filas por dimensión (árbol pivot, paths). */
export function getPivotDimensionGroupKey(
  row: LogisticPivotRow,
  dim: LogisticPivotDimension
): string {
  if (dim === 'item') {
    const typ = row.item_type ?? 'unknown';
    const code = row.item_code ?? '';
    const name = row.item_name ?? '';
    return `${typ}\0${code}\0${name}`;
  }
  const k = DIMENSION_TO_ROW_KEY[dim];
  if (!k) return '__';
  const v = row[k];
  if (v === null || v === undefined) return '__null__';
  return typeof v === 'number' ? String(v) : String(v);
}

/** Comparación lexicográfica de dos filas según una dimensión (ordenación de tabla). */
export function comparePivotRowsByDimension(
  a: LogisticPivotRow,
  b: LogisticPivotRow,
  dim: LogisticPivotDimension
): number {
  if (dim === 'item') {
    const na = a.item_name ?? '';
    const nb = b.item_name ?? '';
    let c = na.localeCompare(nb, undefined, { numeric: true, sensitivity: 'base' });
    if (c !== 0) return c;
    c = String(a.item_type ?? '').localeCompare(String(b.item_type ?? ''));
    if (c !== 0) return c;
    return String(a.item_code ?? '').localeCompare(String(b.item_code ?? ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }
  const rowKey = DIMENSION_TO_ROW_KEY[dim];
  if (!rowKey) return 0;
  const va = a[rowKey];
  const vb = b[rowKey];
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (typeof va === 'number' && typeof vb === 'number') {
    return va - vb;
  }
  return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
}
